import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import WysiwygEditor from '../../../components/common/WysiwygEditor';

function NoticeView() {
  const { noticeId } = useParams();
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const [notice, setNotice] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Update form state
  const [updateTitle, setUpdateTitle] = useState('');
  const [updateText, setUpdateText] = useState('');
  const [updateFile, setUpdateFile] = useState(null);

  useEffect(() => {
    if (user && noticeId) {
      fetchNoticeData();
    }
  }, [user, noticeId]);

  const fetchNoticeData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch CRM notice details via API
      const response = await api.get(`/notices/view/${noticeId}`);
      
      if (response.data.status === 1) {
        const data = response.data.data;
        setNotice(data.row || data);
        setUpdates(data.updates || []);
        setAreas(data.row?.areas || []);
      } else {
        throw new Error(response.data.message || 'Failed to load notice');
      }
    } catch (err) {
      console.error('Error fetching notice:', err);
      setError(err.message);
      toast.error(err.message || 'Failed to load notice');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    const result = await Swal.fire({
      title: 'Resolve Notice',
      text: 'Mark this notice as resolved?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, resolve it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const response = await api.post(`/notices/resolved/${noticeId}`);
        if (response.data.status === 1) {
          toast.success('Notice marked as resolved!');
          fetchNoticeData();
        } else {
          throw new Error(response.data.message || 'Failed to resolve notice');
        }
      } catch (error) {
        console.error('Error resolving notice:', error);
        toast.error('Failed to resolve notice');
      }
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Delete Notice',
      text: 'Are you sure you want to delete this notice? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const response = await api.delete(`/notices/${noticeId}`, {
          params: { name: notice?.notice_title || '' }
        });
        if (response.data.status === 1) {
          toast.success('Notice deleted successfully!');
          navigate('/notices');
        } else {
          throw new Error(response.data.message || 'Failed to delete notice');
        }
      } catch (error) {
        console.error('Error deleting notice:', error);
        toast.error('Failed to delete notice');
      }
    }
  };

  const handleSubmitUpdate = async (e) => {
    e.preventDefault();
    
    if (!updateTitle.trim()) {
      toast.error('Please enter an update title');
      return;
    }

    try {
      setSubmitting(true);
      
      const formData = new FormData();
      formData.append('notice_id', noticeId);
      formData.append('update_title', updateTitle);
      formData.append('update_text', updateText);
      if (updateFile) {
        formData.append('file', updateFile);
      }

      const response = await api.post('/notices/add-update', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.status === 1) {
        toast.success('Update posted successfully!');
        setUpdateTitle('');
        setUpdateText('');
        setUpdateFile(null);
        fetchNoticeData();
      } else {
        throw new Error(response.data.message || 'Failed to post update');
      }
    } catch (error) {
      console.error('Error posting update:', error);
      toast.error('Failed to post update');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUpdate = async (updateId, updateName) => {
    const result = await Swal.fire({
      title: 'Delete Update',
      text: `Are you sure you want to delete "${updateName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const response = await api.delete(`/notices/delete-update/${updateId}`, {
          params: { name: updateName }
        });
        if (response.data.status === 1) {
          toast.success('Update deleted successfully!');
          fetchNoticeData();
        } else {
          throw new Error(response.data.message || 'Failed to delete update');
        }
      } catch (error) {
        console.error('Error deleting update:', error);
        toast.error('Failed to delete update');
      }
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(parseInt(timestamp) * 1000);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatAreasDisplay = () => {
    if (!areas || areas.length === 0) return '';
    
    return areas.map(area => {
      const streets = area.notice_area_streets ? area.notice_area_streets.split(',') : [];
      const subArea = area.sub_area_name ? `: ${area.sub_area_name}: ` : '';
      const streetList = streets.filter(s => s.trim()).join(', ');
      
      return `${area.area_display}${subArea}${streetList}`;
    }).join(', ');
  };

  if (loading) {
    return (
      <div className="p-4 py-6 md:p-8 flex justify-center items-center h-[calc(100vh-150px)]">
        <div 
          className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4" 
          style={{ borderColor: '#0087D4' }}
        ></div>
      </div>
    );
  }

  if (error || !notice) {
    return (
      <div className="p-4 py-6 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-red-800">Error Loading Notice</h3>
                <p className="text-red-700 mt-1">{error || 'Notice not found'}</p>
              </div>
            </div>
            <div className="mt-6 flex space-x-3">
              <button
                onClick={fetchNoticeData}
                style={{ backgroundColor: '#0087D4' }}
                className="text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition duration-150 ease-in-out"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/notices')}
                className="text-center py-2 px-4 rounded-lg text-neutral-600 bg-neutral-200 hover:bg-neutral-300 transition"
              >
                Back to Notices
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 py-6 md:p-8 space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {notice.notice_title}
            {areas && areas.length > 0 && ` in ${formatAreasDisplay()}`}
          </h2>
        </div>
      </div>

      {/* Updates Table */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Updated</th>
                  <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Message</th>
                </tr>
              </thead>
              <tbody>
                {/* Display updates */}
                {updates && updates.map((update) => (
                  <tr key={update.update_id}>
                    <td className="border border-gray-300 px-4 py-2 align-top">
                      {formatDate(update.update_time)}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <h2 className="text-xl font-bold mb-2">{update.update_title}</h2>
                      <div 
                        className="prose max-w-none mb-3"
                        dangerouslySetInnerHTML={{ __html: update.update_text || '' }}
                      />
                      {can('Update Notice') && (
                        <p className="flex gap-2">
                          <Link
                            to={`/notices/edit-update/${update.update_id}`}
                            className="inline-flex items-center px-2 py-1 text-sm text-white rounded"
                            style={{ backgroundColor: '#0087D4' }}
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                          </Link>
                          <button
                            onClick={() => handleDeleteUpdate(update.update_id, update.update_title)}
                            className="inline-flex items-center px-2 py-1 text-sm text-white bg-red-600 rounded hover:bg-red-700"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </button>
                        </p>
                      )}
                    </td>
                  </tr>
                ))}
                
                {/* Original Notice Row */}
                <tr>
                  <td className="border border-gray-300 px-4 py-2 align-top">
                    <strong>Created</strong>
                    <hr className="my-2" />
                    {formatDate(notice.notice_time)}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <div 
                      className="prose max-w-none mb-3"
                      dangerouslySetInnerHTML={{ __html: notice.notice_text || '' }}
                    />
                    <p className="flex gap-2 flex-wrap">
                      {notice.notice_status == 1 && can('Update Notice') && (
                        <button
                          onClick={handleResolve}
                          className="inline-flex items-center px-3 py-2 text-sm text-white bg-green-600 rounded hover:bg-green-700"
                          title="Resolved"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                          </svg>
                          Resolved
                        </button>
                      )}
                      <Link
                        to="edit"
                        className="inline-flex items-center px-3 py-2 text-sm text-white rounded"
                        style={{ backgroundColor: '#fbbf24' }}
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </Link>
                      <Link
                        to={`/dashboard/notices/areas/${noticeId}`}
                        className="inline-flex items-center px-3 py-2 text-sm text-white rounded"
                        style={{ backgroundColor: '#0087D4' }}
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                        </svg>
                        Areas Affected
                      </Link>
                      {can('Delete Notice') && (
                        <button
                          onClick={handleDelete}
                          className="inline-flex items-center px-3 py-2 text-sm text-white bg-red-600 rounded hover:bg-red-700"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete
                        </button>
                      )}
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Post Update Card */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <h3 className="text-xl font-semibold mb-4">Post an update</h3>
          <form onSubmit={handleSubmitUpdate}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
              <input
                type="text"
                value={updateTitle}
                onChange={(e) => setUpdateTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
              <WysiwygEditor
                value={updateText}
                onChange={setUpdateText}
                placeholder="Enter update message..."
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <input
                type="file"
                onChange={(e) => setUpdateFile(e.target.files[0])}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                accept="image/*"
              />
            </div>

            <div className="mb-4">
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-white bg-green-600 rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default NoticeView; 